import { createContext, useEffect, useRef, useState } from 'react';
import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../authConfig';
import { callMsGraph } from '../graph';
import useIsMainWindow from '../hooks/checkMultipleTabs';
import { API_BASE_URL } from '../config/baseURLs';
import { defaultRoutes, filterMenuItems, routes } from '../constant';
import axiosInstance from '../api/axiosInstance';
import { useIdleTimeout } from '../utils';
import Modal from '../components/modal';
import Button from '../components/button';
// import { message } from 'antd';

const ALLOWED_EMAIL_DOMAIN = 'supremecomponents.com';
const IDLE_LOGOUT_SECONDS = 12 * 60 * 60;
const IDLE_PROMPT_SECONDS = 60;

const EdenContext = createContext();

export const EdenProvider = ({ children }) => {
	const { instance } = useMsal();
	const account = useAccount();
	const msalAuthenticated = useIsAuthenticated();

	const userData = account;
	const userMail = userData?.username;
	const isWrongDomain =
		msalAuthenticated &&
		userMail?.substring(userMail.indexOf('@') + 1)?.toLowerCase() !==
			ALLOWED_EMAIL_DOMAIN;
	// A valid MSAL session isn't enough on its own - it also has to be a
	// supremecomponents.com account.
	const isAuthenticated = msalAuthenticated && !isWrongDomain;
	const authError = isWrongDomain ? 'wrong-domain' : null;

	const [me, setMe] = useState(null);
	const fetchUserDataAbortRef = useRef(null);

	const isMain = useIsMainWindow();

	const navigate = useNavigate();

	const [reauthPromptOpen, setReauthPromptOpen] = useState(false);

	const getAccessToken = async () => {
		try {
			const response = await instance.acquireTokenSilent({
				...loginRequest,
				account,
			});
			return response.accessToken;
		} catch (err) {
			if (err instanceof InteractionRequiredAuthError) {
				setReauthPromptOpen(true);
				return null;
			}
			throw err;
		}
	};

	const confirmReauth = () => {
		setReauthPromptOpen(false);
		instance.acquireTokenRedirect({ ...loginRequest, account });
	};

	const requestProfileData = () => {
		getAccessToken()
			.then((accessToken) => accessToken && callMsGraph(accessToken))
			.then((profile) => profile && setMe(profile))
			.catch((err) => {
				console.error('Failed to load MS Graph profile:', err);
			});
	};

	const logout = () => {
		localStorage.removeItem('allowed-routes');
		localStorage.removeItem('customer-journey-animation');
		localStorage.removeItem('checkTab');
		instance.logoutRedirect({ postLogoutRedirectUri: '/' });
	};

	// Signs an authenticated user out after 20 idle minutes, warning 60
	// seconds before with a chance to stay signed in.
	const [idlePromptOpen, setIdlePromptOpen] = useState(false);
	const [idleCountdown, setIdleCountdown] = useState(IDLE_PROMPT_SECONDS);

	const { idleTimer } = useIdleTimeout({
		idleTime: IDLE_LOGOUT_SECONDS,
		promptBeforeIdleTime: IDLE_PROMPT_SECONDS,
		onPrompt: () => {
			if (isAuthenticated) {
				setIdleCountdown(IDLE_PROMPT_SECONDS);
				setIdlePromptOpen(true);
			}
		},
		onIdle: () => {
			setIdlePromptOpen(false);
			if (isAuthenticated) {
				logout();
			}
		},
	});

	useEffect(() => {
		if (!idlePromptOpen) {
			return undefined;
		}

		const interval = setInterval(() => {
			setIdleCountdown((prev) => {
				if (prev <= 1) {
					logout();
					return 0;
				}
				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [idlePromptOpen]);

	const staySignedIn = () => {
		idleTimer.activate();
		setIdlePromptOpen(false);
	};

	const [userInfo, setUserInfo] = useState(null);
	const [userInfoLoading, setUserInfoLoading] = useState(true);
	const [userInfoError, setUserInfoError] = useState(null);
	const [userType, setUserType] = useState('');
	const [cascaderDesignation, setCascaderDesignation] = useState('');
	const [cascaderOption, setCascaderOption] = useState('');
	const [cascaderDesignationMpn, setCascaderDesignationMpn] = useState('');
	const [cascaderOptionMpn, setCascaderOptionMpn] = useState('');
	const [cascaderDesignationCCE, setCascaderDesignationCCE] = useState('');
	const [cascaderOptionCCE, setCascaderOptionCCE] = useState('');
	const [cascaderOptionEE, setCascaderOptionEE] = useState('');
	const [converStatTabEE, setConverStatTabEE] = useState(true);
	const [incomOutEmailTabEE, setIncomeOutEmailTabEE] = useState(true);
	const [converStatTableEE, setConverStatTableEE] = useState(false);
	const [incomOutEmailTableEE, setIncomeOutEmailTableEE] = useState(false);
	const [dateRange, setDateRange] = useState(2);
	// Admin ChatGpt Keywords states
	const [chatGptKeywords, setChatGptKeywords] = useState(null);
	const [chatGptKeywordsLoading, setChatGptKeywordsLoading] = useState(false);
	const [chatGptKeywordsError, setChatGptKeywordsError] = useState(null);
	// Admin Email Exclusion list states
	const [emailExclusionList, setEmailExclusionList] = useState(null);
	const [emailExclusionListLoading, setEmailExclusionListLoading] =
		useState(false);
	const [emailExclusionListError, setEmailExclusionListError] = useState(null);
	// Admin Eagle Preprocessor: Account Tags states
	const [epTagsList, setEpTagsList] = useState(null);
	const [epTagsListLoading, setEpTagsListLoading] = useState(false);
	const [epTagsListError, setEpTagsListError] = useState(null);
	// Admin Sourcing assignments states
	const [sourcingAssList, setSourcingAssList] = useState(null);
	const [sourcingAssListLoading, setSourcingAssListLoading] = useState(false);
	const [sourcingAssListError, setSourcingAssListError] = useState(null);
	// sourcing MPN Cost Book
	const [mpnCostBookList, setMpnCostBookList] = useState([]);
	const [mpnCostBookLoading, setMpnCostBookLoading] = useState(false);
	const [mpnCostBookError, setMpnCostBookError] = useState(null);
	const [costBookType, setCostBookType] = useState(true);
	const [mpnCostBookRowData, setMpnCostBookRowData] = useState([]);
	const [fcExtPriceLoading, setFcExtPriceLoading] = useState(false);
	const [MCBlastUserActiviry, setMCBlastUserActiviry] = useState('');
	const [mpnDateRange, setMpnDateRange] = useState('1W');
	// Bobcat Exclusion list
	const [bobcatExcluList, setBobcatExcluList] = useState(null);
	const [bobcatExcluLoading, setBobcatExcluLoading] = useState(false);
	const [bobcatExcluError, setBobcatExcluError] = useState(null);
	// CRM Accounts data
	const [crmAccounts, setCrmAccounts] = useState([]);
	// CRM Accounts Customer and Supplier data
	const [crmAccCusSupp, setCrmAccCusSupp] = useState([]);
	// SS data
	const [ssData, setSSData] = useState([]);
	// SR data
	const [srData, setSRData] = useState([]);
	// SR data by ID
	const [srDataByEmail, setSrDataByEmail] = useState({});
	// CRM Suppliers data
	const [crmSuppliers, setCrmSuppliers] = useState([]);
	// CRM Country=ies data
	const [crmCountries, setCrmCountries] = useState([]);
	// Message URL
	const [messageURl, setMessageUrl] = useState();
	// sales rep for MPN Queue and MPN Cost Book
	const [cascaderOptions, setCascaderOptions] = useState(null);
	// MPN and Brand
	const [mpnBrand, setMpnBrand] = useState(null);
	// KeywordsforAttachmentIdentification
	const [keywordsforAttachRowData, setKeywordsforAttachRowData] = useState([]);
	// Routes
	const [allowedRoutes, setAllowedRoutes] = useState([]);
	const [menuItems, setMenuItems] = useState(defaultRoutes);
	// Franchise Brands
	const [franchiseBrands, setFranchiseBrands] = useState([]);

	// Fetch routes
	const fetchSectionAccess = (masterId) => {
		fetch(`${API_BASE_URL}/user-section-access/${masterId}&time=${Date.now()}`)
			.then((response) => {
				if (!response.ok) {
					throw new Error(`${response.status}`);
				}
				return response.json();
			})
			.then((accessData) => {
				const apiRoutes = accessData?.routes;
				localStorage.setItem('allowed-routes', accessData);

				if (apiRoutes && apiRoutes?.length > 0) {
					const filtered = filterMenuItems(routes, apiRoutes);
					setMenuItems(filtered.length > 0 ? filtered : defaultRoutes);
					setAllowedRoutes(
						apiRoutes?.length > 0 ? apiRoutes : ['/', '/help-desk'],
					);
				} else {
					setMenuItems(defaultRoutes);
					setAllowedRoutes(['/', '/help-desk']);
				}
			})
			.catch((err) => {
				console.error('Error fetching section access:', err);
				setMenuItems(defaultRoutes);
				setAllowedRoutes(['/', '/help-desk']);
			});
	};

	const fetchUserData = () => {
		// Cancels any in-flight call so a stale request can't race a fresh
		// one and leave userInfoLoading stuck.
		fetchUserDataAbortRef.current?.abort();
		const controller = new AbortController();
		fetchUserDataAbortRef.current = controller;
		const isCurrent = () => fetchUserDataAbortRef.current === controller;

		setUserInfoLoading(true);
		const timeout = setTimeout(() => controller.abort(), 15000);

		fetch(
			`https://internal.supremecomponents.com/api/externalgateway.php?route=getInfoByEmail&email=${userMail}&time=${Date.now()}`,
			{ signal: controller.signal },
		)
			.then((response) => {
				if (!response.ok) {
					throw new Error(`${response.status}`);
				}
				return response.json();
			})
			.then((data) => {
				if (!isCurrent()) return;

				let user = data?.ViewAsLoggedinUser?.Designation;

				if (user === 'Admin') {
					user = 'admin';
				}
				if (user === 'CEO') {
					user = 'ceo';
				}
				if (user === 'Sourcing Specialist') {
					user = 'ss';
				}
				if (user === 'Product Manager') {
					user = 'pm';
				}
				if (user === 'Sales Manager') {
					user = 'sm';
				}
				if (user === 'Sales Rep') {
					user = 'sr';
				}
				setUserType(user);
				setUserInfo(data);
				setUserInfoError(null);

				//  fetch section access
				const masterId = data?.ViewAsLoggedinUser?.MasterId; // or whatever the correct field name is

				if (masterId) {
					fetchSectionAccess(masterId);
				} else {
					console.error('MasterId not found in user data');
				}
			})
			.catch((err) => {
				if (!isCurrent()) return;
				setUserInfoError(err);
				setUserInfo(null);
				setUserInfoLoading(false);
			})
			.finally(() => {
				clearTimeout(timeout);
				if (isCurrent()) {
					setUserInfoLoading(false);
				}
			});
	};

	const crmAccountsData = () => {
		axiosInstance
			.get(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=get_all_crm_accounts',
			)
			.then((response) => {
				setCrmAccounts(response.data);
			});
	};

	const crmAccCusSuppData = () => {
		axiosInstance
			.get(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=get_crm_accounts',
			)
			.then((response) => {
				setCrmAccCusSupp(response.data);
			});
	};

	const getAllSSData = () => {
		axiosInstance
			.get(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=getallss',
			)
			.then((response) => {
				setSSData(response.data);
			});
	};

	const getAllSRData = () => {
		axiosInstance
			.get(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=getallsalesreps',
			)
			.then((response) => {
				setSRData(response.data);
			});
	};

	const getSRDataByEmail = () => {
		axiosInstance
			.get(
				`https://internal.supremecomponents.com/api/externalgateway.php?route=getsalesrepsbyemail&email=${userMail?.toLowerCase()}`,
			)
			.then((response) => {
				setSrDataByEmail(response.data);
			});
	};

	const crmSupplierData = () => {
		axiosInstance
			.get(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=get_all_suppliers',
			)
			.then((response) => {
				setCrmSuppliers(response.data);
			});
	};

	const crmCountriesData = () => {
		axiosInstance
			.get(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=get_crm_country_list',
			)
			.then((response) => {
				setCrmCountries(response.data);
			});
	};

	const getMailUrl = () => {
		axiosInstance
			.get(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=gets3host',
			)
			.then((response) => {
				setMessageUrl(response?.data);
			});
	};

	const getMPNBrand = () => {
		axiosInstance
			.get(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=get_mpn_brand',
			)
			.then((response) => {
				setMpnBrand(response?.data);
			});
	};

	const salesRepForQueueCostBook = () => {
		axiosInstance
			.get(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=gets3host',
			)
			.then((response) => {
				axiosInstance
					.get(
						`https://internal.supremecomponents.com/api/externalgateway.php?route=getInfoByEmail&email=vick@supremecomponents.com`,
					)
					.then((response) => {
						setCascaderOptions(
							response.data?.ViewAsAdmin?.map((el) => ({
								value: el?.MasterId,
								label: el?.SalesRepName,
								children: el?.SalesTeam?.map((el) => ({
									value: el?.MasterId,
									label: el?.SalesRepName,
									children: el?.SalesTeam?.map((el) => ({
										value: el?.MasterId,
										label: el?.SalesRepName,
										children: el?.SalesTeam?.map((el) => ({
											value: el?.MasterId,
											label: el?.SalesRepName,
										})),
									})),
								})),
							})),
						);
					});
			});
	};

	const franchiseBrandData = () => {
		axiosInstance.get(`${API_BASE_URL}/franchiseandpm`).then((response) => {
			setFranchiseBrands(response.data?.data);
		});
	};

	useEffect(() => {
		if (isAuthenticated) {
			requestProfileData();
			fetchUserData();
			setCascaderOption(userMail);
			setCascaderOptionMpn(userMail);
			setCascaderOptionCCE(userMail);
			crmAccountsData();
			crmAccCusSuppData();
			getAllSSData();
			getAllSRData();
			getSRDataByEmail();
			crmSupplierData();
			crmCountriesData();
			getMailUrl();
			salesRepForQueueCostBook();
			getMPNBrand();
			franchiseBrandData();
		} // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated, userMail]);

	//header userTypes
	let userTypes;
	if (
		userInfo?.ViewAsLoggedinUser?.Designation === 'Admin' ||
		userInfo?.ViewAsLoggedinUser?.Designation === 'CEO'
	) {
		userTypes = [
			{ id: '1', type: 'sr', color: 'green' },
			{ id: '2', type: 'sm', color: 'blue' },
			{ id: '4', type: 'ss', color: 'red' },
			{ id: '3', type: 'ceo', color: 'purple' },
			{ id: '5', type: 'admin', color: 'primary' },
		];
	} else if (userInfo?.ViewAsLoggedinUser?.Designation === 'Sales Manager') {
		userTypes = [
			{ id: '1', type: 'sr', color: 'green' },
			{ id: '2', type: 'sm', color: 'blue' },
		];
	} else if (userInfo?.ViewAsLoggedinUser?.Designation === 'Sales Rep') {
		userTypes = [{ id: '1', type: 'sr', color: 'green' }];
	} else if (
		userInfo?.ViewAsLoggedinUser?.Designation === 'Sourcing Specialist'
	) {
		userTypes = [{ id: '1', type: 'ss', color: 'red' }];
	}

	const clearCache = () => {
		localStorage.clear();
		fetch(
			`https://internal.supremecomponents.com/api/externalgateway.php?route=clearcache&time=${Date.now()}`,
		)
			.then((response) => response.json())
			.then(function (data) {
				localStorage.clear();
				navigate('/');
				window.location.reload(false);
			});
	};

	// Admin get all ChatGpt Keywords
	const chatGptAllkeywords = () => {
		const formdata = new FormData();
		formdata.append('is_active', '1');
		setChatGptKeywordsLoading(true);
		axiosInstance
			.post(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=get_all_chatgpt_keyword',
				formdata,
			)
			.then((response) => {
				setChatGptKeywords(response?.data);
				setChatGptKeywordsError(null);
			})
			.catch((err) => {
				setChatGptKeywordsError(err);
				setChatGptKeywords(null);
				setChatGptKeywordsLoading(false);
			})
			.finally(() => {
				setChatGptKeywordsLoading(false);
			});
	};

	// Admin get all Email Exclusion list
	const emailExclusions = () => {
		const formdata = new FormData();
		formdata.append('is_active', '1');
		setEmailExclusionListLoading(true);
		axiosInstance
			.post(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=get_all_excl_email',
				formdata,
			)
			.then((response) => {
				setEmailExclusionList(response?.data);
				setEmailExclusionListError(null);
			})
			.catch((err) => {
				setEmailExclusionListError(err);
				setEmailExclusionList(null);
				setEmailExclusionListLoading(false);
			})
			.finally(() => {
				setEmailExclusionListLoading(false);
			});
	};

	// Admin get all Eagle Preprocessor: Account Tags
	const epTags = () => {
		const formdata = new FormData();
		formdata.append('is_active', '1');
		setEpTagsListLoading(true);
		axiosInstance
			.post(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=get_tags',
				formdata,
			)
			.then((response) => {
				setEpTagsList(response?.data);
				setEpTagsListError(null);
			})
			.catch((err) => {
				setEpTagsListError(err);
				setEpTagsList(null);
				setEpTagsListLoading(false);
			})
			.finally(() => {
				setEpTagsListLoading(false);
			});
	};

	// Admin get all Sourcing Assignments
	const sourcingAssignment = () => {
		setSourcingAssListLoading(true);
		axiosInstance
			.get(
				`https://internal.supremecomponents.com/api/externalgateway.php?route=getallacsourcingreq&email=${userMail}`,
			)
			.then((response) => {
				setSourcingAssList(response?.data);
				setSourcingAssListError(null);
			})
			.catch((err) => {
				setSourcingAssListError(err);
				setSourcingAssList(null);
				setSourcingAssListLoading(false);
			})
			.finally(() => {
				setSourcingAssListLoading(false);
			});
	};

	// Sourcing MPN cost book
	const sourcingMpnQueueCostBook = () => {
		const url = window.location.pathname;
		const sectionName = url
			?.substring(url?.lastIndexOf('/'))
			.replace(/[/-]/g, ' ')
			.toUpperCase()
			.trim();

		let lastUserActivity = '';

		axiosInstance
			.get(
				`https://internal.supremecomponents.com/api/gettracking.php?user_email=${userMail}&section_name=${sectionName}`,
			)
			.then((response) => {
				lastUserActivity = new Date(
					response.data[0]?.SECTION_LEAVE_TIME,
				).toISOString();
				setMCBlastUserActiviry(
					new Date(response.data[0]?.SECTION_LEAVE_TIME).toISOString(),
				);
			})
			.then(() => {
				setMpnCostBookLoading(true);
				axiosInstance
					.get(
						`https://internal.supremecomponents.com/api/externalgateway.php?route=getallmpncostbook&isbuy=${costBookType}&email=${userMail}&lastUserActivity=${lastUserActivity}&interval=${mpnDateRange}`,
					)
					.then((response) => {
						setMpnCostBookList(response?.data);
						setMpnCostBookError(null);
					})
					.catch((err) => {
						setMpnCostBookError(err);
						setMpnCostBookList(null);
						setMpnCostBookLoading(false);
					})
					.finally(() => {
						setMpnCostBookLoading(false);
					});
			});
	};

	// Admin get all Bobcat Exclusion list
	const getBobcatExcluList = () => {
		const formdata = new FormData();
		setBobcatExcluLoading(true);
		axiosInstance
			.post(
				'https://internal.supremecomponents.com/api/externalgateway.php?route=bobcat_get_all_excl_email',
				formdata,
			)
			.then((response) => {
				setBobcatExcluList(response?.data);
				setBobcatExcluError(null);
			})
			.catch((err) => {
				setBobcatExcluError(err);
				setBobcatExcluList(null);
				setBobcatExcluLoading(false);
			})
			.finally(() => {
				setBobcatExcluLoading(false);
			});
	};

	return (
		<EdenContext.Provider
			value={{
				userMail,
				me,
				userData,
				isAuthenticated,
				authError,
				getAccessToken,
				userTypes,
				userType,
				setUserType,
				logout,
				userInfo,
				userInfoLoading,
				userInfoError,
				clearCache,
				cascaderDesignation,
				setCascaderDesignation,
				cascaderOption,
				setCascaderOption,
				cascaderDesignationMpn,
				setCascaderDesignationMpn,
				cascaderOptionMpn,
				setCascaderOptionMpn,
				cascaderDesignationCCE,
				setCascaderDesignationCCE,
				cascaderOptionCCE,
				setCascaderOptionCCE,
				cascaderOptionEE,
				setCascaderOptionEE,
				converStatTabEE,
				setConverStatTabEE,
				incomOutEmailTabEE,
				setIncomeOutEmailTabEE,
				converStatTableEE,
				setConverStatTableEE,
				incomOutEmailTableEE,
				setIncomeOutEmailTableEE,
				chatGptKeywords,
				chatGptKeywordsLoading,
				chatGptKeywordsError,
				chatGptAllkeywords,
				emailExclusionList,
				emailExclusionListLoading,
				emailExclusionListError,
				emailExclusions,
				epTagsList,
				epTagsListLoading,
				epTagsListError,
				epTags,
				crmAccounts,
				crmAccCusSupp,
				ssData,
				srData,
				srDataByEmail,
				sourcingAssList,
				sourcingAssListLoading,
				sourcingAssListError,
				sourcingAssignment,
				mpnCostBookList,
				mpnCostBookLoading,
				mpnCostBookError,
				setMpnCostBookList,
				mpnCostBookRowData,
				setMpnCostBookRowData,
				sourcingMpnQueueCostBook,
				costBookType,
				setCostBookType,
				setMpnDateRange,
				mpnDateRange,
				fcExtPriceLoading,
				setFcExtPriceLoading,
				MCBlastUserActiviry,
				setMCBlastUserActiviry,
				crmSuppliers,
				crmSupplierData,
				crmCountries,
				messageURl,
				dateRange,
				setDateRange,
				bobcatExcluList,
				bobcatExcluLoading,
				bobcatExcluError,
				getBobcatExcluList,
				cascaderOptions,
				mpnBrand,
				keywordsforAttachRowData,
				setKeywordsforAttachRowData,
				isMain,
				allowedRoutes,
				menuItems,
				franchiseBrands,
			}}
		>
			{children}

			<Modal
				open={idlePromptOpen}
				title={<div style={{ textAlign: 'center' }}>Still there?</div>}
				closable={false}
				width={360}
				footer={
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							gap: 10,
							width: '100%',
						}}
					>
						<Button type="bordered" color="dark" onClick={logout}>
							Log out now
						</Button>
						<Button color="dark" onClick={staySignedIn}>
							Stay signed in
						</Button>
					</div>
				}
			>
				<div style={{ textAlign: 'center' }}>
					<p style={{ margin: '0 0 4px', fontSize: 13.5, color: '#6b7280' }}>
						You’ve been inactive for a while. For your security, EDEN will sign
						you out in
					</p>
					<div
						style={{
							fontSize: 48,
							fontWeight: 700,
							lineHeight: 1.1,
							color: '#0c2340',
							margin: '10px 0 2px',
						}}
					>
						{idleCountdown}
					</div>
					<p style={{ margin: '0 0 4px', fontSize: 12, color: '#9ca3af' }}>
						second{idleCountdown === 1 ? '' : 's'}
					</p>
					<p style={{ margin: 0, fontSize: 13.5, color: '#6b7280' }}>
						unless you choose to stay signed in.
					</p>
				</div>
			</Modal>

			<Modal
				open={reauthPromptOpen}
				title={
					<div style={{ textAlign: 'center' }}>Sign-in needs to refresh</div>
				}
				closable={false}
				width={360}
				footer={
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							gap: 10,
							width: '100%',
						}}
					>
						<Button
							type="bordered"
							color="dark"
							onClick={() => setReauthPromptOpen(false)}
						>
							Not now
						</Button>
						<Button color="dark" onClick={confirmReauth}>
							Continue
						</Button>
					</div>
				}
			>
				<p
					style={{
						margin: 0,
						fontSize: 13.5,
						color: '#6b7280',
						textAlign: 'center',
					}}
				>
					Your Microsoft sign-in needs to be renewed to continue. This will
					briefly reload the page, so save any unsaved work first.
				</p>
			</Modal>
		</EdenContext.Provider>
	);
};

export default EdenContext;
